import React from "react";
import ConfirmPopup from "../../ConfirmPopup";
import type { PublishCatalogPopupState } from "./usePublishCatalogPopup";
import type { UpgradeQueuedPayload } from "./useEpoUpgradeTracker";
import { AlertNotification, fontFamilies, Text } from "@commonsku/styles";
import { CatalogUpgradeProductPopup } from "./CatalogUpgradeProductPopup";

interface CatalogPublishPopupsProps {
    popupState: PublishCatalogPopupState | null;
    publishing?: boolean;
    catalogId: string | null;
    onEpoWarningCancel: () => void;
    onEpoWarningUpgrade: () => void;
    onUpgradeQueued: (payload: UpgradeQueuedPayload) => void;
    onPublishWithoutUpgrade: () => void;
    onPublishConfirmCancel: () => void;
    onPublishConfirm: () => void;
    onPublishSuccessDone: () => void;
    onClose: () => void;
}

export const CatalogPublishPopups = ({
    popupState,
    publishing = false,
    catalogId,
    onEpoWarningCancel,
    onEpoWarningUpgrade,
    onUpgradeQueued,
    onPublishWithoutUpgrade,
    onPublishConfirmCancel,
    onPublishConfirm,
    onPublishSuccessDone,
    onClose,
}: CatalogPublishPopupsProps) => {
    if (!popupState) {
        return null;
    }

    switch (popupState.step) {
        case "epoWarning":
            return (
                <ConfirmPopup
                    title="Upgrade for ePO before publishing"
                    titleStyle={{ fontWeight: 600 }}
                    cancelLabel="Publish without upgrading"
                    confirmLabel="Upgrade to ePO"
                    onClose={onEpoWarningCancel}
                    onConfirm={onEpoWarningUpgrade}
                >
                    <Text style={{ fontFamily: fontFamilies.skuFontMedium, fontWeight: 400 }}>
                        Get live inventory, custom pricing and real-time order status for this product.
                    </Text>
                    <AlertNotification alertType="warn">
                        <Text color="secondary2" style={{ fontFamily: fontFamilies.skuFontMedium, fontWeight: 400 }}>
                            Upgrading after publishing requires unpublishing the product,
                            which will invalidate external SKUs and may break connected systems.
                            We recommend upgrading now.
                        </Text>
                    </AlertNotification>
                </ConfirmPopup>
            );

        case "upgradeEpo":
            if (!popupState.convertibleProduct) return null;
            return (
                <CatalogUpgradeProductPopup
                    item={popupState.item}
                    convertibleProduct={popupState.convertibleProduct}
                    catalogId={catalogId}
                    onClose={onClose}
                    onQueued={onUpgradeQueued}
                    onPublishWithoutUpgrade={
                        popupState.mode === "publish" ? onPublishWithoutUpgrade : undefined
                    }
                />
            );

        case "publishConfirm":
            return (
                <ConfirmPopup
                    title="Publish Product to Client Catalog"
                    cancelLabel="Cancel"
                    confirmLabel="Yes, Publish"
                    confirmLoading={publishing}
                    onClose={onPublishConfirmCancel}
                    onConfirm={onPublishConfirm}
                >
                    <Text style={{ fontFamily: fontFamilies.skuFontMedium }}>
                        This product will become visible to all sales reps building projects for this client.
                    </Text>
                    <AlertNotification alertType="warn">
                        <Text color="secondary2"  style={{ fontFamily: fontFamilies.skuFontMedium }}>
                            Once published, the following fields will lock and cannot be edited without admin permission:
                            <ul>
                                <li>SKUs and external SKUs</li>
                                <li>Size and color variants</li>
                                <li>Decoration details — imprint type, logo name, logo size, logo color, placement</li>
                                <li>Item location name</li>
                            </ul>
                        </Text>
                    </AlertNotification>
                </ConfirmPopup>
            );
        case "publishSuccess":
            return (
                <ConfirmPopup
                    title="Publish Successful"
                    confirmLabel="Done"
                    onConfirm={onPublishSuccessDone}
                >
                    <AlertNotification alertType="success">
                        <Text color="secondary3"  style={{ fontFamily: fontFamilies.skuFontMedium }}>
                            Item has been published to the Client Catalog and is now visible to sales reps.
                            You can find it in the <strong>Published</strong> tab.
                        </Text>
                    </AlertNotification>
                </ConfirmPopup>
            );
        default:
            return null;
    }
};
